/**
 * Authentication and permission utility functions
 */
import { getServices } from '../firebase.js';

/**
 * Check if a user has admin permissions
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} - True if the user is an admin
 */
export const isUserAdmin = async (userId) => {
  if (!userId) return false;
  
  try {
    const { db } = getServices();
    const userDoc = await db.collection('users').doc(userId).get();
    
    if (!userDoc.exists) return false;
    
    const userData = userDoc.data();
    return userData.role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Check if a user is the creator of a tournament
 * @param {string} userId - The user ID to check
 * @param {string} tournamentId - The tournament ID
 * @returns {Promise<boolean>} - True if the user is the tournament creator
 */
export const isTournamentCreator = async (userId, tournamentId) => {
  if (!userId || !tournamentId) return false;
  
  try {
    const { db } = getServices();
    const tournamentDoc = await db.collection('tournaments').doc(tournamentId).get();
    
    if (!tournamentDoc.exists) return false;
    
    const tournamentData = tournamentDoc.data();
    return tournamentData.creatorId === userId;
  } catch (error) {
    console.error('Error checking tournament creator status:', error);
    return false;
  }
};

/**
 * Check if a user has permission to edit a tournament
 * @param {string} userId - The user ID to check
 * @param {string} tournamentId - The tournament ID
 * @returns {Promise<boolean>} - True if the user can edit the tournament
 */
export const canEditTournament = async (userId, tournamentId) => {
  if (!userId || !tournamentId) return false;
  
  try {
    // Check if user is admin or tournament creator
    const [admin, creator] = await Promise.all([
      isUserAdmin(userId),
      isTournamentCreator(userId, tournamentId)
    ]);
    
    return admin || creator;
  } catch (error) {
    console.error('Error checking tournament edit permissions:', error);
    return false;
  }
};

/**
 * Check if a user can register for a tournament
 * @param {string} userId - The user ID to check
 * @param {string} tournamentId - The tournament ID
 * @returns {Promise<{canRegister: boolean, reason: string|null}>} - Result and reason if can't register
 */
export const canRegisterForTournament = async (userId, tournamentId) => {
  if (!userId || !tournamentId) {
    return { canRegister: false, reason: 'Invalid user or tournament ID' };
  }
  
  try {
    const { db } = getServices();
    const tournamentDoc = await db.collection('tournaments').doc(tournamentId).get();
    
    if (!tournamentDoc.exists) {
      return { canRegister: false, reason: 'Tournament not found' };
    }
    
    const tournamentData = tournamentDoc.data();
    
    // Check if tournament is full
    if (tournamentData.currentTeams >= tournamentData.maxTeams) {
      return { canRegister: false, reason: 'Tournament is full' };
    }
    
    // Check if registration is open
    if (tournamentData.status !== 'upcoming' && tournamentData.status !== 'registration') {
      return { canRegister: false, reason: 'Registration is closed for this tournament' };
    }
    
    // Check if user is already registered
    if (tournamentData.participants && tournamentData.participants.includes(userId)) {
      return { canRegister: false, reason: 'Already registered for this tournament' };
    }
    
    // Check if registration deadline has passed
    if (tournamentData.registrationDeadline) {
      const deadline = tournamentData.registrationDeadline.toDate();
      if (deadline < new Date()) {
        return { canRegister: false, reason: 'Registration deadline has passed' };
      }
    }
    
    return { canRegister: true, reason: null };
  } catch (error) {
    console.error('Error checking tournament registration eligibility:', error);
    return { canRegister: false, reason: 'Error checking eligibility' };
  }
};

/**
 * Check if a user is a participant in a tournament
 * @param {string} userId - The user ID to check
 * @param {string} tournamentId - The tournament ID
 * @returns {Promise<boolean>} - True if the user is a participant
 */
export const isParticipantInTournament = async (userId, tournamentId) => {
  if (!userId || !tournamentId) return false;
  
  try {
    const { db } = getServices();
    const tournamentDoc = await db.collection('tournaments').doc(tournamentId).get();
    
    if (!tournamentDoc.exists) return false;
    
    const tournamentData = tournamentDoc.data();
    return tournamentData.participants && tournamentData.participants.includes(userId);
  } catch (error) {
    console.error('Error checking tournament participation:', error);
    return false;
  }
}; 